/**
 * Branded identifiers, provider enumerations and record-key helpers.
 *
 * Agency identity (users, accounts, devices, runs, ...) uses UUIDs. Provider objects use stable
 * composite keys: a tenant-scoped RecordKey for anything synced into an account store and a
 * provider-qualified CanonicalKey for shared master tests / classification rules (spec section 5).
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------------------------
// UUID helpers
// ---------------------------------------------------------------------------------------------

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** RFC 4122 shaped identifier (any version, case-insensitive). */
export const UuidSchema = z.string().regex(UUID_PATTERN, 'expected a UUID');
export type Uuid = z.infer<typeof UuidSchema>;

/** New random UUID v4 (Node 19+, Electron, browsers). */
export function newId(): string {
  return globalThis.crypto.randomUUID();
}

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

function uuidId<B extends string>() {
  return UuidSchema.brand<B>();
}

function stringId<B extends string>(max = 256) {
  return z.string().min(1).max(max).brand<B>();
}

// ---------------------------------------------------------------------------------------------
// Agency identity ids (UUID)
// ---------------------------------------------------------------------------------------------

export const UserIdSchema = uuidId<'UserId'>();
export type UserId = z.infer<typeof UserIdSchema>;
export const AccountIdSchema = uuidId<'AccountId'>();
export type AccountId = z.infer<typeof AccountIdSchema>;
export const ConnectionIdSchema = uuidId<'ConnectionId'>();
export type ConnectionId = z.infer<typeof ConnectionIdSchema>;
export const ConnectionDraftIdSchema = uuidId<'ConnectionDraftId'>();
export type ConnectionDraftId = z.infer<typeof ConnectionDraftIdSchema>;
export const DeviceIdSchema = uuidId<'DeviceId'>();
export type DeviceId = z.infer<typeof DeviceIdSchema>;
export const SessionIdSchema = uuidId<'SessionId'>();
export type SessionId = z.infer<typeof SessionIdSchema>;
export const InvitationIdSchema = uuidId<'InvitationId'>();
export type InvitationId = z.infer<typeof InvitationIdSchema>;
export const RunIdSchema = uuidId<'RunId'>();
export type RunId = z.infer<typeof RunIdSchema>;
export const DatasetRunIdSchema = uuidId<'DatasetRunId'>();
export type DatasetRunId = z.infer<typeof DatasetRunIdSchema>;
export const SnapshotIdSchema = uuidId<'SnapshotId'>();
export type SnapshotId = z.infer<typeof SnapshotIdSchema>;
export const OperationIdSchema = uuidId<'OperationId'>();
export type OperationId = z.infer<typeof OperationIdSchema>;
export const TemplateVersionIdSchema = uuidId<'TemplateVersionId'>();
export type TemplateVersionId = z.infer<typeof TemplateVersionIdSchema>;
export const ContentReleaseIdSchema = uuidId<'ContentReleaseId'>();
export type ContentReleaseId = z.infer<typeof ContentReleaseIdSchema>;
export const AiKeyIdSchema = uuidId<'AiKeyId'>();
export type AiKeyId = z.infer<typeof AiKeyIdSchema>;
export const AiKeyAssignmentIdSchema = uuidId<'AiKeyAssignmentId'>();
export type AiKeyAssignmentId = z.infer<typeof AiKeyAssignmentIdSchema>;
export const GrantIdSchema = uuidId<'GrantId'>();
export type GrantId = z.infer<typeof GrantIdSchema>;
export const LeaseIdSchema = uuidId<'LeaseId'>();
export type LeaseId = z.infer<typeof LeaseIdSchema>;
export const NotificationIdSchema = uuidId<'NotificationId'>();
export type NotificationId = z.infer<typeof NotificationIdSchema>;
export const AuditEventIdSchema = uuidId<'AuditEventId'>();
export type AuditEventId = z.infer<typeof AuditEventIdSchema>;
export const UsageRecordIdSchema = uuidId<'UsageRecordId'>();
export type UsageRecordId = z.infer<typeof UsageRecordIdSchema>;
export const ArtifactIdSchema = uuidId<'ArtifactId'>();
export type ArtifactId = z.infer<typeof ArtifactIdSchema>;
export const AgendaIdSchema = uuidId<'AgendaId'>();
export type AgendaId = z.infer<typeof AgendaIdSchema>;
export const OutreachBatchIdSchema = uuidId<'OutreachBatchId'>();
export type OutreachBatchId = z.infer<typeof OutreachBatchIdSchema>;
export const OutreachItemIdSchema = uuidId<'OutreachItemId'>();
export type OutreachItemId = z.infer<typeof OutreachItemIdSchema>;
export const RumiSessionIdSchema = uuidId<'RumiSessionId'>();
export type RumiSessionId = z.infer<typeof RumiSessionIdSchema>;
export const RumiTurnIdSchema = uuidId<'RumiTurnId'>();
export type RumiTurnId = z.infer<typeof RumiTurnIdSchema>;
export const IssueIdSchema = uuidId<'IssueId'>();
export type IssueId = z.infer<typeof IssueIdSchema>;
export const OverrideIdSchema = uuidId<'OverrideId'>();
export type OverrideId = z.infer<typeof OverrideIdSchema>;
export const OutboxItemIdSchema = uuidId<'OutboxItemId'>();
export type OutboxItemId = z.infer<typeof OutboxItemIdSchema>;
export const MailDeliveryIdSchema = uuidId<'MailDeliveryId'>();
export type MailDeliveryId = z.infer<typeof MailDeliveryIdSchema>;
export const ExportIdSchema = uuidId<'ExportId'>();
export type ExportId = z.infer<typeof ExportIdSchema>;
export const BatchIdSchema = uuidId<'BatchId'>();
export type BatchId = z.infer<typeof BatchIdSchema>;

// ---------------------------------------------------------------------------------------------
// Human / provider assigned ids (non-UUID strings)
// ---------------------------------------------------------------------------------------------

/** Content item id, e.g. `agenda.default` or `email.policies_training`. */
export const TemplateIdSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9._-]{0,127}$/, 'template ids are lowercase slugs')
  .brand<'TemplateId'>();
export type TemplateId = z.infer<typeof TemplateIdSchema>;
/** Provider-owned workspace id (Drata workspace, Vanta organisation). */
export const WorkspaceIdSchema = stringId<'WorkspaceId'>(128);
export type WorkspaceId = z.infer<typeof WorkspaceIdSchema>;
/** Provider-owned tenant identifier (Vanta organisation id, Drata account id). */
export const TenantIdSchema = stringId<'TenantId'>(128);
export type TenantId = z.infer<typeof TenantIdSchema>;
/** Provider-owned object id as returned by the API. */
export const SourceIdSchema = stringId<'SourceId'>(512);
export type SourceId = z.infer<typeof SourceIdSchema>;
/** Outreach task group id, e.g. `policies_training`, `mfa`, `custom:<sourceId>`. */
export const TaskGroupIdSchema = stringId<'TaskGroupId'>(256);
export type TaskGroupId = z.infer<typeof TaskGroupIdSchema>;
/** Renderer workspace tab id. */
export const TabIdSchema = stringId<'TabId'>(128);
export type TabId = z.infer<typeof TabIdSchema>;
/** Release cohort id (`cohort_3`, `cohort_10`, `all`). */
export const CohortIdSchema = z
  .string()
  .regex(/^[a-z0-9_-]{1,64}$/)
  .brand<'CohortId'>();
export type CohortId = z.infer<typeof CohortIdSchema>;
/** Rule id: a CanonicalKey string (shared master test) — kept separate for readability. */
export const RuleIdSchema = stringId<'RuleId'>(1024);
export type RuleId = z.infer<typeof RuleIdSchema>;

// ---------------------------------------------------------------------------------------------
// Providers, object types, datasets
// ---------------------------------------------------------------------------------------------

export const PROVIDERS = ['vanta', 'drata'] as const;
export const ProviderSchema = z.enum(PROVIDERS);
export type Provider = z.infer<typeof ProviderSchema>;

export const ACCOUNT_PROVIDERS = ['vanta', 'drata', 'template_only'] as const;
export const AccountProviderSchema = z.enum(ACCOUNT_PROVIDERS);
export type AccountProvider = z.infer<typeof AccountProviderSchema>;

export const EXTERNAL_SERVICES = ['vanta', 'drata', 'google', 'openai', 'agency'] as const;
export const ExternalServiceSchema = z.enum(EXTERNAL_SERVICES);
export type ExternalService = z.infer<typeof ExternalServiceSchema>;

export const OBJECT_TYPES = [
  'test',
  'control',
  'document',
  'policy',
  'framework',
  'vulnerability',
  'person',
  'computer',
  'integration',
  'vendor',
  'monitor',
  'evidence',
  'workspace',
  'test_entity',
  'monitor_failure',
] as const;
export const ObjectTypeSchema = z.enum(OBJECT_TYPES);
export type ObjectType = z.infer<typeof ObjectTypeSchema>;

export const VANTA_DATASETS = [
  'tests',
  'test_entities',
  'documents',
  'policies',
  'frameworks',
  'framework_controls',
  'control_tests',
  'control_documents',
  'framework_tests',
  'framework_documents',
  'vulnerabilities',
  'people',
  'monitored_computers',
  'integrations',
  'vendors',
] as const;
export const DRATA_DATASETS = [
  'workspaces',
  'controls',
  'frameworks',
  'monitors',
  'monitor_failures',
  'evidence',
  'personnel',
] as const;
export const DATASET_IDS = [...VANTA_DATASETS, ...DRATA_DATASETS] as const;
export const DatasetIdSchema = z.enum(DATASET_IDS);
export type DatasetId = z.infer<typeof DatasetIdSchema>;
export type VantaDatasetId = (typeof VANTA_DATASETS)[number];
export type DrataDatasetId = (typeof DRATA_DATASETS)[number];

export function datasetsForProvider(provider: Provider): readonly DatasetId[] {
  return provider === 'vanta' ? VANTA_DATASETS : DRATA_DATASETS;
}

/** Which object type each dataset primarily yields. */
export const DATASET_OBJECT_TYPE: Record<DatasetId, ObjectType> = {
  tests: 'test',
  test_entities: 'test_entity',
  documents: 'document',
  policies: 'policy',
  frameworks: 'framework',
  framework_controls: 'control',
  control_tests: 'test',
  control_documents: 'document',
  framework_tests: 'test',
  framework_documents: 'document',
  vulnerabilities: 'vulnerability',
  people: 'person',
  monitored_computers: 'computer',
  integrations: 'integration',
  vendors: 'vendor',
  workspaces: 'workspace',
  controls: 'control',
  monitors: 'monitor',
  monitor_failures: 'monitor_failure',
  evidence: 'evidence',
  personnel: 'person',
};

// ---------------------------------------------------------------------------------------------
// Record keys
// ---------------------------------------------------------------------------------------------

const KEY_SEPARATOR = ':';
const EMPTY_WORKSPACE = '_';

/** Percent-encode the characters that would break key parsing (`%` and `:`). */
export function encodeKeyPart(part: string): string {
  return part.replace(/%/g, '%25').replace(/:/g, '%3A');
}

export function decodeKeyPart(part: string): string {
  return part.replace(/%3A/gi, ':').replace(/%25/g, '%');
}

/**
 * Provider-qualified canonical key for shared master tests and classification rules:
 * `provider:objectType:catalogId`. The catalog id is the provider's verified generic identifier
 * (Vanta test id, Drata control code), never a tenant-specific row id.
 */
export const CanonicalKeySchema = z
  .string()
  .regex(/^(vanta|drata):[a-z_]+:[^:]+$/, 'expected provider:objectType:catalogId')
  .brand<'CanonicalKey'>();
export type CanonicalKey = z.infer<typeof CanonicalKeySchema>;

export function canonicalRecordKey(provider: Provider, objectType: ObjectType, catalogId: string): CanonicalKey {
  if (catalogId.length === 0) throw new Error('catalogId must not be empty');
  return [provider, objectType, encodeKeyPart(catalogId)].join(KEY_SEPARATOR) as CanonicalKey;
}

export interface ParsedCanonicalKey {
  provider: Provider;
  objectType: ObjectType;
  catalogId: string;
}

export function parseCanonicalKey(key: string): ParsedCanonicalKey | null {
  const parts = key.split(KEY_SEPARATOR);
  if (parts.length !== 3) return null;
  const [provider, objectType, catalogId] = parts;
  const p = ProviderSchema.safeParse(provider);
  const o = ObjectTypeSchema.safeParse(objectType);
  if (!p.success || !o.success || !catalogId) return null;
  return { provider: p.data, objectType: o.data, catalogId: decodeKeyPart(catalogId) };
}

/**
 * Tenant-scoped record key for synced objects:
 * `provider:accountId:workspaceId|_:objectType:sourceId`.
 */
export const RecordKeySchema = z
  .string()
  .regex(
    /^(vanta|drata):[0-9a-fA-F-]{36}:[^:]+:[a-z_]+:[^:]+$/,
    'expected provider:accountId:workspaceId:objectType:sourceId',
  )
  .brand<'RecordKey'>();
export type RecordKey = z.infer<typeof RecordKeySchema>;

export interface RecordKeyParts {
  provider: Provider;
  accountId: AccountId;
  workspaceId?: string | null;
  objectType: ObjectType;
  sourceId: string;
}

export function recordKey(parts: RecordKeyParts): RecordKey {
  if (parts.sourceId.length === 0) throw new Error('sourceId must not be empty');
  const workspace = parts.workspaceId ? encodeKeyPart(parts.workspaceId) : EMPTY_WORKSPACE;
  return [parts.provider, parts.accountId, workspace, parts.objectType, encodeKeyPart(parts.sourceId)].join(
    KEY_SEPARATOR,
  ) as RecordKey;
}

export interface ParsedRecordKey {
  provider: Provider;
  accountId: AccountId;
  workspaceId: string | null;
  objectType: ObjectType;
  sourceId: string;
}

export function parseRecordKey(key: string): ParsedRecordKey | null {
  const parts = key.split(KEY_SEPARATOR);
  if (parts.length !== 5) return null;
  const [provider, accountId, workspace, objectType, sourceId] = parts;
  const p = ProviderSchema.safeParse(provider);
  const a = AccountIdSchema.safeParse(accountId);
  const o = ObjectTypeSchema.safeParse(objectType);
  if (!p.success || !a.success || !o.success || !sourceId || !workspace) return null;
  return {
    provider: p.data,
    accountId: a.data,
    workspaceId: workspace === EMPTY_WORKSPACE ? null : decodeKeyPart(workspace),
    objectType: o.data,
    sourceId: decodeKeyPart(sourceId),
  };
}

/** Prefix that selects every record of one account (and optionally one workspace). */
export function recordKeyPrefix(provider: Provider, accountId: AccountId, workspaceId?: string | null): string {
  const workspace = workspaceId ? encodeKeyPart(workspaceId) : EMPTY_WORKSPACE;
  return [provider, accountId, workspace, ''].join(KEY_SEPARATOR);
}

/**
 * Raw vulnerability finding key (spec section 9): provider + account/workspace + finding id + asset + package.
 * Grouping by package name is presentation only; this is the storage identity.
 */
export function vulnerabilityFindingKey(input: {
  provider: Provider;
  accountId: AccountId;
  workspaceId?: string | null;
  findingId: string;
  assetId: string;
  packageIdentifier?: string | null;
}): RecordKey {
  const sourceId = [input.findingId, input.assetId, input.packageIdentifier ?? ''].map(encodeKeyPart).join('|');
  return recordKey({
    provider: input.provider,
    accountId: input.accountId,
    workspaceId: input.workspaceId ?? null,
    objectType: 'vulnerability',
    sourceId,
  });
}
